import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  CartesianGrid, ReferenceDot, ComposedChart,
} from "recharts";
import {
  Lock, LogOut, Download, RefreshCw, Users, TrendingUp,
  DollarSign, Target, ShieldCheck, Sparkles, AlertTriangle,
  Loader2, Trash2, FileText, ChevronDown, KeyRound, Smartphone, MapPin,
} from "lucide-react";
import { ContinuityCard } from "../components/admin/ContinuityCard";
import { QrCode } from "lucide-react";
import { OperatingExpensesCard } from "../components/admin/OperatingExpensesCard";
import { ExpenseVsRevenueCard } from "../components/admin/ExpenseVsRevenueCard";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";
const EVIQ_LOGO = "https://customer-assets.emergentagent.com/job_1702c522-5b25-4ef8-9648-e8665ef2da24/artifacts/adnelyb3_EVIQ%20Logo.png";

// ---- API helpers (always send credentials for httpOnly cookie flow) -----
const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const api = {
  login: (email, password) =>
    fetch(`${API}/api/admin/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    }),
  me:      () => fetch(`${API}/api/admin/me`,       { credentials: "include", headers: authHeaders(), cache: "no-store" }),
  logout:  () => fetch(`${API}/api/admin/logout`,   { method: "POST", credentials: "include", headers: authHeaders() }),
  dashboard: (fresh) => fetch(`${API}/api/admin/dashboard?_t=${Date.now()}${fresh ? "&fresh=true" : ""}`, { credentials: "include", headers: authHeaders(), cache: "no-store" }),
  users:     (fresh) => fetch(`${API}/api/admin/users?_t=${Date.now()}${fresh ? "&fresh=true" : ""}`,     { credentials: "include", headers: authHeaders(), cache: "no-store" }),
  reportSales: () => fetch(`${API}/api/admin/report-sales?_t=${Date.now()}`, { credentials: "include", headers: authHeaders(), cache: "no-store" }),
  markPaid:  (email, paid) => fetch(`${API}/api/admin/users/mark-paid`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ email, paid }),
  }),
  getCosts:  () => fetch(`${API}/api/admin/costs`,    { credentials: "include", headers: authHeaders() }),
  saveCosts: (body) => fetch(`${API}/api/admin/costs`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }),
  complianceUrl: (key) => `${API}/api/admin/compliance/${key}.pdf`,
  complianceZipUrl: () => `${API}/api/admin/compliance/all.zip`,
  getManifest: () => fetch(`${API}/api/admin/compliance/manifest`,   { credentials: "include", headers: authHeaders() }),
  getLlc:      () => fetch(`${API}/api/admin/llc-config`,             { credentials: "include", headers: authHeaders() }),
  saveLlc:     (body) => fetch(`${API}/api/admin/llc-config`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }),
  downloadPdf: (period) => `${API}/api/admin/report.pdf?period=${period}`,
};

// ---- Currency + percent formatters --------------------------------------
const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const usdShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};
const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

// ---- Time range picker used on each chart card --------------------------
const RANGE_OPTIONS = [
  { value: 30,   label: "30d" },
  { value: 60,   label: "60d" },
  { value: 90,   label: "90d" },
  { value: 180,  label: "180d" },
  { value: 365,  label: "1Y"  },
  { value: 730,  label: "2Y"  },
  { value: 1095, label: "3Y"  },
  { value: 1460, label: "4Y"  },
  { value: 1825, label: "5Y"  },
];

const RangeSelect = ({ value, onChange, testid }) => (
  <div className="relative shrink-0">
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      aria-label="Time range"
      className="appearance-none bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg pl-2.5 pr-6 py-1 text-[10px] font-mono uppercase tracking-wider text-white transition-colors cursor-pointer"
    >
      {RANGE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value} className="bg-black">
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown className="w-3 h-3 text-zinc-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
  </div>
);

// Filter helpers — signups is per-day, revenue+cash are per-month.
const filterSignups = (rows, days) => {
  if (!rows) return [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return rows.filter((r) => new Date(r.day) >= cutoff);
};
const filterMonthly = (rows, days) => {
  if (!rows) return [];
  const months = Math.max(1, Math.ceil(days / 30));
  return rows.slice(-months);
};
// Sum revenue rows within the last N days for income statement.
// opex uses REAL logged expenses (expense_series) for months that have
// entries; the manual cost estimates only fill months with nothing logged.
const aggregateIncome = (revenue_series, days, costs, expense_series) => {
  const months = Math.max(1, Math.ceil(days / 30));
  const rows = (revenue_series || []).slice(-months);
  const revenue = rows.reduce((a, r) => a + (r.basic_mrr || r.monthly_mrr || 0) + (r.yearly_mrr || 0) + (r.lifetime_rev || 0), 0);
  const fixed = (costs?.hosting_monthly || 0) + (costs?.salaries_monthly || 0) +
                (costs?.marketing_monthly || 0) + (costs?.tools_monthly || 0) +
                (costs?.other_fixed_monthly || 0);
  const expByMonth = Object.fromEntries((expense_series || []).map((e) => [e.month, e.total]));
  const opex = rows.length
    ? rows.reduce((a, r) => a + (expByMonth[r.month] ?? fixed), 0)
    : fixed * months;
  const cogs = revenue * ((costs?.cogs_pct_of_revenue || 0) / 100);
  return { revenue, cogs, opex, net_income: revenue - cogs - opex };
};

// ============================================================================
// LOGIN SCREEN
// ============================================================================
const LoginCard = ({ onSuccess }) => {
  const [email, setEmail] = useState("admin@eviq.pro");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [challenge, setChallenge] = useState(null);  // null until 2FA is requested
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.login(email.trim(), password);
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) setError("Too many attempts. Try again in 15 minutes.");
        else setError(typeof data.detail === "string" ? data.detail : "Login failed");
      } else if (data.totp_required) {
        setChallenge(data.challenge_token);
      } else {
        if (data.access_token) localStorage.setItem(AUTH_KEY, data.access_token);
        onSuccess();
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login/totp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_token: challenge, code: totpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) setError("Too many 2FA attempts. Try again in 15 minutes.");
        else setError(data.detail || "Invalid code");
      } else {
        if (data.access_token) localStorage.setItem(AUTH_KEY, data.access_token);
        onSuccess();
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5" data-testid="admin-login">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <img
            src={EVIQ_LOGO}
            alt="EVIQ"
            className="w-11 h-11 rounded-xl shrink-0 object-cover"
            data-testid="admin-login-logo"
          />
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              EVIQ · Admin
            </div>
            <div className="font-display text-2xl text-white font-semibold">
              {challenge ? "Enter 6-digit code" : "Sign in"}
            </div>
          </div>
        </div>

        {!challenge ? (
          <form onSubmit={submitPassword} className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email" autoComplete="username" required
                className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-xl px-4 py-3 text-white font-mono text-sm transition-colors" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 block">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password" autoComplete="current-password" required
                className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-xl px-4 py-3 text-white font-mono text-sm transition-colors" />
            </div>
            {error && (
              <div data-testid="admin-error" className="rounded-lg border border-[#FF6B57]/30 bg-[#FF6B57]/[0.08] px-3 py-2 text-sm text-[#FF6B57] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
                {error}
              </div>
            )}
            <button type="submit" data-testid="admin-signin" disabled={loading}
              className="w-full brand-gradient text-black font-semibold rounded-xl px-4 py-3 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" strokeWidth={2.5} />}
              {loading ? "Signing in…" : "Sign in securely"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitTotp} className="space-y-4">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Open your authenticator app (Google Authenticator, Authy, 1Password) and enter the 6-digit code for EVIQ Admin. Or use a one-time recovery code.
            </p>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 block">6-digit code or recovery code</label>
              <input value={totpCode} onChange={(e) => setTotpCode(e.target.value)} autoFocus
                data-testid="admin-totp-code" autoComplete="one-time-code" required
                inputMode="numeric" placeholder="000000"
                className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-xl px-4 py-3 text-white font-mono text-lg tracking-widest text-center transition-colors" />
            </div>
            {error && (
              <div data-testid="admin-error" className="rounded-lg border border-[#FF6B57]/30 bg-[#FF6B57]/[0.08] px-3 py-2 text-sm text-[#FF6B57] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
                {error}
              </div>
            )}
            <button type="submit" data-testid="admin-totp-submit" disabled={loading}
              className="w-full brand-gradient text-black font-semibold rounded-xl px-4 py-3 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" strokeWidth={2.5} />}
              {loading ? "Verifying…" : "Verify & sign in"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setTotpCode(""); }}
              className="w-full text-xs text-zinc-500 hover:text-white transition-colors py-1">
              ← Start over
            </button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-white/5 text-[11px] text-zinc-600 leading-relaxed">
          {challenge
            ? "Two-factor authentication is enabled. Codes rotate every 30 seconds."
            : "Access limited to the account owner. bcrypt(12) + JWT-HS256 · httpOnly cookies · rate-limited."}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DASHBOARD
// ============================================================================
const KpiTile = ({ icon: Icon, label, value, sub, tone = "neutral", testid }) => {
  const toneClasses = {
    ok:      "border-[#39FF14]/25 bg-[#39FF14]/[0.04]",
    warn:    "border-[#FFB020]/25 bg-[#FFB020]/[0.04]",
    bad:     "border-[#FF6B57]/25 bg-[#FF6B57]/[0.04]",
    neutral: "border-white/10 bg-white/[0.02]",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border ${toneClasses[tone]} p-4 sm:p-5`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 truncate">
          {label}
        </span>
      </div>
      <div className="text-white font-semibold text-2xl sm:text-3xl leading-none">
        {value}
      </div>
      {sub && (
        <div className="mt-1.5 text-[11px] font-mono text-zinc-500">{sub}</div>
      )}
    </div>
  );
};

const SCAN_SOURCE_LABELS = { banner: "banner", card: "card", app_qr_legacy: "card·legacy", untagged: "untagged" };

const QrScanTile = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/scan/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => {});
  }, []);
  const breakdown = stats?.sources
    ? Object.entries(stats.sources)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${SCAN_SOURCE_LABELS[k] || k} ${v}`)
        .join(" · ")
    : "";
  return (
    <KpiTile
      icon={QrCode}
      label="QR scans"
      value={stats ? stats.total : "…"}
      sub={stats ? `${stats.today} today${breakdown ? ` · ${breakdown}` : ` · ${stats.last_30} last 30d`}` : "physical marketing"}
      testid="kpi-qr-scans"
    />
  );
};

/** Mini sparkline — scans per day over the last 30 days, best day called out. */
const ScanTrendTile = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/scan/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => {});
  }, []);

  const series = useMemo(() => {
    const byDate = Object.fromEntries((stats?.days || []).map((d) => [d.date, d.count || 0]));
    const out = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push({
        date: key,
        label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        count: byDate[key] || 0,
      });
    }
    return out;
  }, [stats]);

  const best = useMemo(
    () => series.reduce((a, r) => (r.count > (a?.count || 0) ? r : a), null),
    [series]);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 col-span-2" data-testid="scan-trend-tile">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">Scans per day · 30d</span>
        </div>
        {best?.count > 0 && (
          <span className="font-mono text-[9px] text-[#39FF14]" data-testid="scan-trend-best">
            Best: {best.label} · {best.count}
          </span>
        )}
      </div>
      <div className="h-14" data-testid="scan-trend-sparkline">
        <ResponsiveContainer>
          <AreaChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="scanSparkFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#39FF14" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#39FF14" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={[0, (max) => Math.max(2, max)]} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: "#999" }}
              formatter={(v) => [`${v} scan${v === 1 ? "" : "s"}`, null]}
            />
            <Area type="monotone" dataKey="count" stroke="#39FF14" strokeWidth={1.5} fill="url(#scanSparkFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/** Where QR-banner scans come from — top cities with a Google Maps link. */
const ScanLocationsTile = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/api/admin/scan/locations?_t=${Date.now()}`, { credentials: "include", headers: authHeaders(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeArea = async (l) => {
    if (!window.confirm(`Remove all ${l.count} scan${l.count === 1 ? "" : "s"} recorded for ${l.area}? This also rolls back the scan counters.`)) return;
    try {
      await fetch(`${API}/api/admin/scan/locations/remove`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ cities: l.cities || [] }),
      });
    } catch {}
    load();
  };
  const locs = data?.locations || [];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 col-span-2" data-testid="scan-locations-tile">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">Scan locations</span>
      </div>
      {data === null ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : locs.length === 0 ? (
        <div className="text-[11px] text-zinc-500 leading-relaxed" data-testid="scan-locations-empty">
          No located scans yet — city capture starts with the next banner scan.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="scan-locations-list">
          {locs.slice(0, 3).map((l) => (
            <div key={l.area} className="flex items-center justify-between gap-2">
              <a
                href={l.lat != null && l.lon != null
                  ? `https://www.google.com/maps?q=${l.lat},${l.lon}`
                  : `https://www.google.com/maps?q=${encodeURIComponent(`${l.area} supercharger`)}`}
                target="_blank" rel="noopener noreferrer"
                data-testid={`scan-location-link-${l.area}`}
                className="inline-flex items-center gap-1.5 text-xs text-white hover:text-[#00E5FF] transition-colors truncate"
                title={`${(l.cities || []).join(", ")} — open on Google Maps`}
              >
                <MapPin className="w-3 h-3 shrink-0 text-[#00E5FF]" strokeWidth={2.2} />
                <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-[#39FF14]/35 text-[#39FF14] bg-[#39FF14]/[0.06] shrink-0">
                  {l.area}
                </span>
                {(l.cities || []).length > 0 && l.cities[0] !== l.area && (
                  <span className="truncate text-zinc-400 text-[11px]">{l.cities[0]}</span>
                )}
              </a>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="font-mono text-[10px] text-zinc-400">{l.count}×</span>
                <button
                  onClick={() => removeArea(l)}
                  data-testid={`scan-location-remove-${l.area}`}
                  title={`Remove ${l.area} scans (test/noise cleanup)`}
                  className="w-4 h-4 rounded flex items-center justify-center text-zinc-600 hover:text-[#FF6B57] hover:bg-white/5 transition-colors text-[11px] leading-none"
                >
                  ×
                </button>
              </span>
            </div>
          ))}
          {locs.length > 3 && (
            <div className="font-mono text-[9px] text-zinc-600">+{locs.length - 3} more areas</div>
          )}
        </div>
      )}
    </div>
  );
};

const txTone = (status) =>
  status === "succeeded" ? "#39FF14" : status === "refunded" ? "#FFB020" : "#FF6B57";

// Bucket helpers for the revenue chart — monthly ("May 26") or weekly (week
// starting Monday, "May 25").
const weekStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const buildRevenueBuckets = (transactions, mode) => {
  const buckets = new Map();
  for (const tx of transactions || []) {
    if (tx.status !== "succeeded" && tx.status !== "refunded") continue;
    const d = new Date(tx.created);
    const anchor = mode === "weekly" ? weekStart(d) : new Date(d.getFullYear(), d.getMonth(), 1);
    const key = anchor.getTime();
    if (!buckets.has(key)) {
      buckets.set(key, {
        ts: key,
        label: mode === "weekly"
          ? anchor.toLocaleDateString(undefined, { month: "short", day: "numeric" })
          : anchor.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
        gross: 0, net: 0, count: 0,
      });
    }
    const b = buckets.get(key);
    b.gross += tx.amount || 0;
    b.net += (tx.amount || 0) - (tx.amount_refunded || 0);
    b.count += 1;
  }
  const rows = [...buckets.values()].sort((a, b) => a.ts - b.ts);
  return rows.slice(mode === "weekly" ? -16 : -12).map((r) => ({
    ...r, gross: +r.gross.toFixed(2), net: +r.net.toFixed(2),
  }));
};

/** Revenue trend — styled to match the Projected Sales area chart:
 *  layered gradient areas (gross/net) + payments line, executive stat strip,
 *  monthly/weekly toggle. */
const StripeRevenueChartCard = ({ data }) => {
  const [mode, setMode] = useState("monthly");
  const rows = useMemo(() => buildRevenueBuckets(data?.transactions, mode), [data, mode]);
  const totals = useMemo(
    () => rows.reduce((a, r) => ({ gross: a.gross + r.gross, net: a.net + r.net, count: a.count + r.count }),
      { gross: 0, net: 0, count: 0 }),
    [rows]);

  const stats = [
    { label: "Gross revenue", value: usd(totals.gross), tone: "#00E5FF" },
    { label: "Net revenue", value: usd(totals.net), tone: "#39FF14" },
    { label: "Payments", value: totals.count.toLocaleString(), tone: "#FFB020" },
    { label: "Avg / payment", value: totals.count ? usd(totals.net / totals.count) : "—", tone: "#fff" },
  ];

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="stripe-revenue-chart-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
          Revenue · live from Stripe
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5">
          {["monthly", "weekly"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`revenue-chart-${m}-toggle`}
              className={`px-3 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-colors ${
                mode === m ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-3">
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">{s.label}</div>
            <div className="font-mono text-lg sm:text-xl mt-1 font-medium" style={{ color: s.tone }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="h-72">
        {rows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-xs font-mono" data-testid="revenue-chart-empty">
            No Stripe revenue yet.
          </div>
        ) : (
          <ResponsiveContainer data-testid="revenue-chart-body">
            <ComposedChart data={rows}>
              <defs>
                <linearGradient id="revGrossFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="revNetFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#39FF14" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#39FF14" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} />
              <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={usdShort} width={50} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} />
              <Tooltip
                contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                formatter={(v, name) => (name === "Payments" ? [v, name] : [usd(v), name])}
              />
              <Area yAxisId="left" type="monotone" dataKey="gross" stroke="#00E5FF" fill="url(#revGrossFill)" strokeWidth={2} name="Gross revenue" />
              <Area yAxisId="left" type="monotone" dataKey="net" stroke="#39FF14" fill="url(#revNetFill)" strokeWidth={2} name="Net revenue" />
              <Line yAxisId="right" type="monotone" dataKey="count" stroke="#FFB020" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Payments" />
              <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

/** Live Stripe transactions — pulled from the Stripe API (auto-refreshes
 *  every 60s so new payments post to the dashboard on their own). */
const StripePanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/stripe/transactions?limit=300&_t=${Date.now()}`, {
        credentials: "include", headers: authHeaders(), cache: "no-store",
      });
      if (!r.ok) throw new Error(`Stripe fetch failed (${r.status})`);
      setData(await r.json());
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e.message || "Couldn't reach Stripe");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-4">
      <StripeRevenueChartCard data={data} />
      <StripeTransactionsCard data={data} error={error} loading={loading} load={load} updatedAt={updatedAt} />
    </div>
  );
};

const StripeTransactionsCard = ({ data, error, loading, load, updatedAt }) => {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="stripe-transactions-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
            Transactions · live from Stripe
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mt-1">
            {data?.count ? `${data.count} payment${data.count === 1 ? "" : "s"}` : "Live payments only"} · auto-refreshes every 60s
            {updatedAt ? ` · updated ${updatedAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="stripe-tx-refresh"
          className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-[10px] font-mono uppercase tracking-wider text-zinc-300 disabled:opacity-50">
          {loading ? "…" : "Refresh"}
        </button>
      </div>
      {error && <div className="text-[11px] text-[#FF6B57] font-mono mb-2" data-testid="stripe-tx-error">{error}</div>}
      {data?.mode === "test" && (
        <div className="mb-3 rounded-lg border border-[#FFB020]/40 bg-[#FFB020]/[0.06] px-3 py-2 text-[11px] text-[#FFB020] font-mono" data-testid="stripe-test-key-warning">
          Stripe TEST-mode key configured — live transactions can't be shown here. Set a live
          STRIPE_SECRET_KEY (or STRIPE_LIVE_SECRET_KEY) in production to see real payments.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10">
              {["When", "Customer", "Amount", "Status", "Description", ""].map((h) => (
                <th key={h} className="px-2.5 py-2 font-mono text-[9px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="stripe-tx-rows">
            {data === null ? (
              <tr><td colSpan={6} className="px-2.5 py-5 text-center text-zinc-500 text-xs">Loading transactions…</td></tr>
            ) : (data.transactions || []).length === 0 ? (
              <tr><td colSpan={6} className="px-2.5 py-5 text-center text-zinc-500 text-xs">No Stripe transactions yet.</td></tr>
            ) : data.transactions.slice(0, 25).map((tx) => (
              <tr key={tx.id} className="border-b border-white/5">
                <td className="px-2.5 py-2 text-[11px] font-mono text-zinc-400 whitespace-nowrap">
                  {new Date(tx.created).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </td>
                <td className="px-2.5 py-2 text-xs text-white max-w-[180px] truncate">{tx.email || "—"}</td>
                <td className="px-2.5 py-2 text-xs font-mono text-white whitespace-nowrap">${tx.amount.toFixed(2)}</td>
                <td className="px-2.5 py-2">
                  <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
                    style={{ color: txTone(tx.status), borderColor: `${txTone(tx.status)}55`, background: `${txTone(tx.status)}10` }}>
                    {tx.status}
                  </span>
                </td>
                <td className="px-2.5 py-2 text-[11px] text-zinc-400 max-w-[200px] truncate">{tx.description || "—"}</td>
                <td className="px-2.5 py-2">
                  {tx.receipt_url && (
                    <a href={tx.receipt_url} target="_blank" rel="noopener noreferrer"
                      className="font-mono text-[9px] uppercase tracking-wider text-[#00E5FF] hover:text-white">
                      Receipt
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** Failed-payment recovery panel — recent retry emails + manual "Run now". */
const RecoveryPanel = () => {
  const [log, setLog] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadLog = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/payment-recovery/log?_t=${Date.now()}`, {
        credentials: "include", headers: authHeaders(), cache: "no-store",
      });
      if (!r.ok) throw new Error(`Log fetch failed (${r.status})`);
      setLog(await r.json());
    } catch (e) {
      setError(e.message || "Couldn't load recovery log");
    }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const runNow = async () => {
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/payment-recovery/run`, {
        method: "POST", credentials: "include", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`Run failed (${r.status})`);
      const j = await r.json();
      setResult(j);
      loadLog();
    } catch (e) {
      setError(e.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const rows = log?.sent || [];
  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="recovery-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
            Failed-payment recovery
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mt-1">
            Declined cards get one friendly retry email · runs hourly · {rows.length} sent so far
          </p>
        </div>
        <button onClick={runNow} disabled={running} data-testid="recovery-run-now"
          className="px-3.5 py-1.5 rounded-lg brand-gradient text-black font-semibold text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50">
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" strokeWidth={2.5} />}
          Run now
        </button>
      </div>

      {error && <div className="text-[11px] text-[#FF6B57] font-mono mb-2" data-testid="recovery-error">{error}</div>}
      {result && (
        <div className="mb-3 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] px-3 py-2 text-[11px] font-mono text-[#00E5FF]" data-testid="recovery-run-result">
          Scan complete — {result.checked} declined customer{result.checked === 1 ? "" : "s"} checked · {result.sent} email{result.sent === 1 ? "" : "s"} sent
          {result.checked > 0 && result.sent === 0 ? " · everyone either recovered on their own or was already emailed" : ""}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10">
              {["Customer", "Plan", "Card declined", "Email sent"].map((h) => (
                <th key={h} className="px-2.5 py-2 font-mono text-[9px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="recovery-log-rows">
            {log === null ? (
              <tr><td colSpan={4} className="px-2.5 py-5 text-center text-zinc-500 text-xs">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-2.5 py-5 text-center text-zinc-500 text-xs">
                No recovery emails sent yet — they'll appear here when a declined customer needs a nudge.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.charge_id} className="border-b border-white/5">
                <td className="px-2.5 py-2 text-xs text-white max-w-[200px] truncate">{r.email}</td>
                <td className="px-2.5 py-2">
                  <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-300">
                    {r.plan_id || "unknown"}
                  </span>
                </td>
                <td className="px-2.5 py-2 text-[11px] font-mono text-zinc-400 whitespace-nowrap">{fmt(r.failed_at)}</td>
                <td className="px-2.5 py-2 text-[11px] font-mono text-[#39FF14] whitespace-nowrap">{fmt(r.sent_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** Top referral users — emails + award detail, admin-only. */
const TopReferrersPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${API}/api/admin/referrals/top?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => { if (!r.ok) throw new Error(`Fetch failed (${r.status})`); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);
  const rows = data?.rows || [];
  const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="top-referrers-panel">
      <div className="mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Top referral users</div>
        <p className="font-mono text-[10px] text-zinc-600 mt-1">
          Free month requires an active subscription · 1 per quarter · max 4 per year
        </p>
      </div>
      {error && <div className="text-[11px] text-[#FF6B57] font-mono mb-2">{error}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10">
              {["Referrer", "Referrals", "Months earned", "Subscription", "Last referral"].map((h) => (
                <th key={h} className="px-2.5 py-2 font-mono text-[9px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="top-referrers-rows">
            {data === null && !error ? (
              <tr><td colSpan={5} className="px-2.5 py-5 text-center text-zinc-500 text-xs">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-2.5 py-5 text-center text-zinc-500 text-xs">
                No referrals yet — they'll appear when a friend subscribes through a member's link.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.email} className="border-b border-white/5">
                <td className="px-2.5 py-2 text-xs text-white max-w-[220px] truncate">
                  {r.email}
                  {r.display_name && <span className="text-zinc-500 ml-1.5">"{r.display_name}"</span>}
                </td>
                <td className="px-2.5 py-2 text-xs font-mono text-white">{r.referrals}</td>
                <td className="px-2.5 py-2 text-xs font-mono text-[#39FF14]">{r.months_earned}</td>
                <td className="px-2.5 py-2">
                  <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
                    style={r.active_subscription
                      ? { color: "#39FF14", borderColor: "#39FF1455", background: "#39FF1410" }
                      : { color: "#FF6B57", borderColor: "#FF6B5755", background: "#FF6B5710" }}>
                    {r.active_subscription ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-2.5 py-2 text-[11px] font-mono text-zinc-400 whitespace-nowrap">{fmtDay(r.last_referral_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** Cancellation feedback — reasons users give when canceling. Hidden until
 *  the first response exists. */
const REASON_LABELS = {
  too_expensive: "Too expensive", not_using: "Not using it enough",
  missing_feature: "Missing a feature", sold_ev: "Sold their EV", other: "Something else",
};
const CancelFeedbackCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/admin/cancel-feedback?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);
  if (!data || data.count === 0) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="cancel-feedback-card">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Cancellation feedback</div>
      <p className="font-mono text-[10px] text-zinc-600 mb-4">{data.count} response{data.count === 1 ? "" : "s"} · why users cancel</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(data.counts).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/[0.03] border border-white/10 font-mono text-[10px]">
            <span className="text-white">{REASON_LABELS[k] || k}</span>
            <span className="text-[#FFB020]">{v}</span>
          </span>
        ))}
      </div>
      <div className="space-y-2" data-testid="cancel-feedback-rows">
        {data.rows.slice(0, 8).map((r, i) => (
          <div key={i} className="rounded-xl border border-white/5 bg-black/30 px-3.5 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-white truncate">{r.email || "anonymous"}</span>
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#FFB020] shrink-0">{REASON_LABELS[r.reason] || r.reason}</span>
            </div>
            {r.note && <div className="text-[11px] text-zinc-400 mt-1 italic">"{r.note}"</div>}
            <div className="font-mono text-[9px] text-zinc-600 mt-1">
              {new Date(r.ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/** OEM waitlist demand — notify-me signups per brand. */
const WaitlistDemandCard = () => {
  const [data, setData] = useState(null);
  const [launching, setLaunching] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/admin/oem/waitlist?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const launch = async (make) => {
    if (!window.confirm(`Send the "${make} is LIVE" launch email to every pending waitlister now? This can't be undone.`)) return;
    setLaunching(make);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/oem/launch`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ make }),
      });
      const j = await r.json();
      setResult(r.ok
        ? `${make}: ${j.sent} launch email${j.sent === 1 ? "" : "s"} sent${j.failed ? ` · ${j.failed} failed` : ""}`
        : `Launch failed (${r.status})`);
      load();
    } catch {
      setResult("Launch failed — network error");
    } finally {
      setLaunching("");
    }
  };

  const brands = data?.brands || [];
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="waitlist-demand-card">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">OEM waitlist demand</div>
      <p className="font-mono text-[10px] text-zinc-600 mb-4">
        Notify-me signups per brand · {data?.total ?? "…"} total · launch emails go out the day you flip a brand live
      </p>
      {result && (
        <div className="mb-3 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] px-3 py-2 text-[11px] font-mono text-[#00E5FF]" data-testid="waitlist-launch-result">
          {result}
        </div>
      )}
      {data === null ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : brands.length === 0 ? (
        <div className="text-xs text-zinc-500" data-testid="waitlist-empty">
          No waitlist signups yet — they'll appear when visitors tap "Notify" on a roadmap card.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="waitlist-brand-grid">
          {brands.map((b) => (
            <div key={b.make} className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-3 flex flex-col" data-testid={`waitlist-brand-${b.make}`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500 truncate">{b.make}</div>
              <div className="font-mono text-xl mt-1 font-medium text-[#00E5FF]">{b.count}</div>
              <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
                {b.launched_at
                  ? `launched · ${b.notified} emailed`
                  : `${b.pending} waiting${b.last_signup ? ` · last ${new Date(b.last_signup).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}`}
              </div>
              {b.pending > 0 ? (
                <button
                  onClick={() => launch(b.make)}
                  disabled={launching === b.make}
                  data-testid={`waitlist-launch-${b.make}`}
                  className="mt-2.5 rounded-lg py-1.5 text-[9px] font-mono uppercase tracking-wider brand-gradient text-black font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {launching === b.make ? "Sending…" : "Go live → email all"}
                </button>
              ) : b.launched_at ? (
                <div className="mt-2.5 rounded-lg py-1.5 text-center text-[9px] font-mono uppercase tracking-wider border border-[#39FF14]/30 text-[#39FF14]">
                  Live ✓
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const SectionCard = ({ title, right, children, testid }) => (  <div
    data-testid={testid}
    className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6"
  >
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [costs, setCosts] = useState(null);
  const [costsDirty, setCostsDirty] = useState(false);

  // Per-chart time range windows (independent)
  const [rangeSignups, setRangeSignups] = useState(90);
  const [rangeRevenue, setRangeRevenue] = useState(180);
  const [rangeCashflow, setRangeCashflow] = useState(180);
  const [rangeIncome, setRangeIncome] = useState(30);
  const [projectionHorizon, setProjectionHorizon] = useState(12);   // months
  const [manifest, setManifest] = useState([]);
  const [llc, setLlc] = useState(null);
  const [llcDirty, setLlcDirty] = useState(false);
  const [llcOpen, setLlcOpen] = useState(false);
  const [liveUsers, setLiveUsers] = useState(null);
  const [reportSales, setReportSales] = useState(null);

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError("");
    try {
      const [d, c, mf, llc, u, rs] = await Promise.all([
        api.dashboard(fresh),
        api.getCosts(),
        api.getManifest(),
        api.getLlc(),
        api.users(fresh),
        api.reportSales(),
      ]);
      if (d.status === 401 || c.status === 401) {
        onLogout();
        return;
      }
      const dashData = await d.json();
      const costsData = await c.json();
      const manifestData = mf.ok ? await mf.json() : { reports: [] };
      const llcData = llc.ok ? await llc.json() : {};
      setLiveUsers(u.ok ? await u.json() : null);
      setReportSales(rs.ok ? await rs.json() : null);
      setData(dashData);
      setCosts(costsData);
      setManifest(manifestData.reports || []);
      setLlc(llcData);
      setLlcDirty(false);
      setCostsDirty(false);
    } catch {
      setError("Failed to load. Check your connection and retry.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    await api.logout();
    localStorage.removeItem(AUTH_KEY);
    onLogout();
  };
  const saveCosts = async () => {
    setBusy(true);
    await api.saveCosts(costs);
    await load();
    setBusy(false);
  };

  const saveLlc = async () => {
    setBusy(true);
    await api.saveLlc(llc);
    await load();
    setBusy(false);
  };

  const downloadCompliance = (key) => {
    const url = key === "all" ? api.complianceZipUrl() : api.complianceUrl(key);
    const token = localStorage.getItem(AUTH_KEY);
    fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("Download failed");
        const blob = await r.blob();
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = key === "all" ? "EVIQ_Compliance_Package.zip" : `EVIQ_${key}.pdf`;
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
      })
      .catch(() => setError("Compliance download failed"));
  };

  const downloadPdf = (period) => {
    // Trigger a native download using cookie auth (Bearer added via a form is
    // too complex — we rely on the cookie set at login for authorization)
    const url = api.downloadPdf(period);
    const token = localStorage.getItem(AUTH_KEY);
    // Cookie flow works directly; also send Bearer as fallback via fetch → blob
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error("Download failed");
        const blob = await r.blob();
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = `EVIQ_${period}_financials.pdf`;
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
      })
      .catch(() => setError("PDF download failed"));
  };

  const kpis = data?.kpis || {};
  const be = data?.break_even || {};
  const proj = data?.projections || {};

  // Client-side filtered series based on each chart's range
  const signupsFiltered = useMemo(() => filterSignups(data?.signups_series, rangeSignups), [data, rangeSignups]);
  const revenueFiltered = useMemo(() => filterMonthly(data?.revenue_series, rangeRevenue), [data, rangeRevenue]);
  const cashFiltered    = useMemo(() => filterMonthly(data?.cash_series, rangeCashflow), [data, rangeCashflow]);
  const incomeThis      = useMemo(() => aggregateIncome(data?.revenue_series, rangeIncome, costs, data?.expense_series), [data, rangeIncome, costs]);
  const incomeYtd       = data?.income_statement?.ytd || {};

  // Break-even crossover: find the first x where revenue >= cost
  const breakEvenPoint = useMemo(() => {
    if (!be.series?.length) return null;
    for (const row of be.series) {
      if (row.revenue >= row.cost) return row;
    }
    return be.series[be.series.length - 1];
  }, [be]);

  const planDonut = [
    { name: "Basic", value: kpis.basic_subscribers ?? kpis.monthly_subscribers ?? 0, color: "#39FF14" },
    { name: "Yearly", value: kpis.yearly_subscribers || 0, color: "#00E5FF" },
    { name: "Lifetime", value: kpis.lifetime_subscribers || 0, color: "#C084FC" },
    { name: "Trialing", value: kpis.trialing || 0, color: "#FFB020" },
    { name: "Pending", value: kpis.pending_checkout || 0, color: "#666" },
  ].filter((s) => s.value > 0);
  const totalSubs = planDonut.reduce((a, s) => a + s.value, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={EVIQ_LOGO}
              alt="EVIQ"
              className="w-9 h-9 rounded-lg shrink-0 object-cover"
              data-testid="admin-header-logo"
            />
            <div className="min-w-0">
              <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-zinc-500 leading-tight">EVIQ · Admin</div>
              <div className="text-xs sm:text-sm text-white font-medium leading-tight truncate">Financial dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { load(true); setRefreshKey((k) => k + 1); }}
              data-testid="admin-refresh"
              className="p-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors text-zinc-400"
              title="Refresh all dashboard data"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => {
                // Owner-only shortcut: unlock the paid mobile experience
                // for the person holding admin credentials and pop /app in a
                // new tab so they can test end-to-end without a Bluevine
                // subscription. This flag lives in localStorage and can be
                // toggled off from the banner shown at the top of /app.
                try {
                  window.localStorage.setItem("eviq.owner_mode", "1");
                } catch (_e) {}
                window.open("/app", "_blank", "noopener");
              }}
              data-testid="admin-preview-as-owner"
              className="px-2.5 sm:px-3 py-2 rounded-lg border border-[#FFB020]/40 bg-[#FFB020]/[0.06] hover:bg-[#FFB020]/[0.12] transition-colors text-[#FFB020] text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 sm:gap-2"
              title="Open /app with the paywall bypassed"
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Preview app</span>
            </button>
            <a
              href="/admin/impact"
              data-testid="admin-impact-link"
              className="px-2.5 sm:px-3 py-2 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] hover:bg-[#00E5FF]/[0.1] transition-colors text-[#00E5FF] text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 sm:gap-2"
            >
              <span className="hidden sm:inline">Veteran Impact</span><span className="sm:hidden">VI</span>
            </a>
            <a
              href="/admin/blog"
              data-testid="admin-blog-link"
              className="px-2.5 sm:px-3 py-2 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/[0.04] hover:bg-[#39FF14]/[0.1] transition-colors text-[#39FF14] text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 sm:gap-2"
            >
              <span className="hidden sm:inline">Blog</span><span className="sm:hidden">BL</span>
            </a>
            <a
              href="/admin/warranty"
              data-testid="admin-warranty-link"
              className="px-2.5 sm:px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors text-zinc-400 text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 sm:gap-2"
            >
              <span className="hidden sm:inline">Warranty DB</span><span className="sm:hidden">WDB</span>
            </a>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="px-2.5 sm:px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors text-zinc-400 text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 sm:gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6">
        {error && (
          <div className="rounded-xl border border-[#FF6B57]/30 bg-[#FF6B57]/[0.06] px-4 py-3 text-sm text-[#FF6B57]">
            {error}
          </div>
        )}

        {/* PDF export bar */}
        <div className="rounded-2xl border border-[#39FF14]/25 bg-[#39FF14]/[0.03] p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="admin-pdf-bar">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 shrink-0" style={{ color: "#39FF14" }} strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-sm text-white font-medium">
                Export for accountant / auditor
              </div>
              <div className="text-[11px] sm:text-xs text-zinc-400 mt-0.5 leading-tight">
                Multi-page PDF · KPIs · income statement · cash flow · break-even · costs
              </div>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => downloadPdf("monthly")}
              data-testid="admin-pdf-monthly"
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 hover:border-white/25 text-xs font-mono uppercase tracking-wider text-white transition-colors inline-flex items-center justify-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> Monthly
            </button>
            <button
              onClick={() => downloadPdf("annual")}
              data-testid="admin-pdf-annual"
              className="flex-1 sm:flex-none brand-gradient text-black font-semibold px-3 sm:px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> Annual
            </button>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="admin-kpi-grid">
          <KpiTile icon={Users} label="Paid users" value={kpis.total_paid_users || 0}
                   sub={`${kpis.basic_subscribers ?? kpis.monthly_subscribers ?? 0} basic · ${kpis.yearly_subscribers || 0} yr · ${kpis.lifetime_subscribers || 0} life`}
                   tone="ok" testid="kpi-users" />
          <KpiTile icon={TrendingUp} label="MRR" value={usdShort(kpis.mrr)}
                   sub={`${usd(kpis.mrr)} recurring / month`} testid="kpi-mrr" />
          <KpiTile icon={TrendingUp} label="ARR" value={usdShort(kpis.arr)}
                   sub={usd(kpis.arr)} testid="kpi-arr" />
          <KpiTile icon={DollarSign} label="Lifetime revenue" value={usdShort(kpis.lifetime_revenue)}
                   sub={usd(kpis.lifetime_revenue)} testid="kpi-lifetime" />
          <KpiTile icon={Sparkles} label="ARPU / user" value={usd(kpis.arpu)}
                   sub="per month, blended" testid="kpi-arpu" />
          <KpiTile icon={Target} label="Trial → paid" value={pct(kpis.conversion_rate)}
                   sub={`${kpis.in_trial_now || 0} in 7-day trial now`} testid="kpi-conv" />
          <KpiTile icon={Users}
                   label="Signups vs cancels"
                   value={`+${kpis.signups_30d || 0} / −${kpis.cancellations_30d || 0}`}
                   sub={`last 30d · ${kpis.canceled_total || 0} canceled all-time`}
                   tone={(kpis.cancellations_30d || 0) > (kpis.signups_30d || 0) ? "warn" : "ok"}
                   testid="kpi-churn" />
          <QrScanTile key={`qr-${refreshKey}`} />
          <ScanLocationsTile key={`loc-${refreshKey}`} />
          <ScanTrendTile key={`trend-${refreshKey}`} />
        </div>

        <StripePanel key={`stripe-${refreshKey}`} />

        <RecoveryPanel key={`recovery-${refreshKey}`} />

        <TopReferrersPanel key={`referrers-${refreshKey}`} />

        <CancelFeedbackCard key={`cancelfb-${refreshKey}`} />

        <WaitlistDemandCard key={`waitlist-${refreshKey}`} />

        {/* Live user accounts — demo seed data excluded */}
        <SectionCard
          testid="live-users"
          title="User accounts · live (demo data excluded)"
          right={
            <span className="text-[10px] font-mono text-zinc-500">
              {liveUsers?.as_of ? `as of ${new Date(liveUsers.as_of).toLocaleTimeString()}` : ""}
            </span>
          }
        >
          {!liveUsers ? (
            <div className="text-xs text-zinc-500 font-mono">Loading live accounts…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiTile icon={Users} label="Total accounts" value={liveUsers.total_accounts}
                         sub="unique emails" tone="ok" testid="kpi-live-total" />
                <KpiTile icon={DollarSign} label="Verified paid" value={liveUsers.paid_accounts}
                         sub={`${liveUsers.stripe_verified_accounts || 0} Stripe-verified · ${liveUsers.unpaid_accounts} pending`} tone="ok" testid="kpi-live-paid" />
                <KpiTile icon={Sparkles} label="Tesla connected" value={liveUsers.tesla_connected}
                         sub="active OAuth sessions" testid="kpi-live-tesla" />
                <KpiTile icon={Target} label="Recall alert emails" value={liveUsers.recall_alert_emails}
                         sub="opted in" testid="kpi-live-recall" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs" data-testid="live-users-table">
                  <thead>
                    <tr className="text-zinc-500 font-mono uppercase text-[10px] tracking-wider border-b border-white/10">
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Plan</th>
                      <th className="py-2 pr-3">Amount</th>
                      <th className="py-2 pr-3">Payment</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Created</th>
                      <th className="py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveUsers.users.map((u) => (
                      <tr key={u.email} className="border-b border-white/5" data-testid={`live-user-${u.email}`}>
                        <td className="py-2 pr-3 text-white">{u.email}</td>
                        <td className="py-2 pr-3 font-mono">{u.plan_id || "—"}</td>
                        <td className="py-2 pr-3 font-mono">{u.amount != null ? usd(u.amount) : "—"}</td>
                        <td className="py-2 pr-3">
                          {u.stripe_verified ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono"
                                  style={{ background: "rgba(57,255,20,0.1)", color: "#39FF14", border: "1px solid rgba(57,255,20,0.3)" }}
                                  title="Matched to a succeeded live Stripe payment">
                              PAID · STRIPE ✓
                            </span>
                          ) : u.paid ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono"
                                  style={{ background: "rgba(0,229,255,0.08)", color: "#00E5FF", border: "1px solid rgba(0,229,255,0.3)" }}
                                  title="Marked paid manually — no matching live Stripe payment found">
                              PAID · MANUAL
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono"
                                  style={{ color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)" }}>
                              UNVERIFIED
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-zinc-400">{u.subscription_status || u.payment_status || "—"}</td>
                        <td className="py-2 pr-3 font-mono text-zinc-500">{u.source}</td>
                        <td className="py-2 pr-3 font-mono text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                        <td className="py-2">
                          <button
                            type="button"
                            data-testid={`mark-paid-${u.email}`}
                            onClick={async () => {
                              const r = await api.markPaid(u.email, !u.paid);
                              if (r.ok) {
                                const fresh = await api.users();
                                if (fresh.ok) setLiveUsers(await fresh.json());
                              }
                            }}
                            className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wide transition-colors"
                            style={u.paid
                              ? { color: "#a1a1aa", border: "1px solid rgba(255,255,255,0.15)" }
                              : { color: "#000", background: "linear-gradient(90deg,#39FF14,#00E5FF)", fontWeight: 700 }}
                          >
                            {u.paid ? "Revoke" : "Mark paid"}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {liveUsers.users.length === 0 && (
                      <tr>
                        <td colSpan="8" className="py-4 text-zinc-500 font-mono">No live accounts yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SectionCard>

        {/* One-off certified report sales (buyer flow) */}
        <SectionCard
          testid="report-sales"
          title="Certified report sales · one-off buyer purchases"
          right={
            <span className="text-[10px] font-mono text-zinc-500">
              {reportSales?.as_of ? `as of ${new Date(reportSales.as_of).toLocaleTimeString()}` : ""}
            </span>
          }
        >
          {!reportSales ? (
            <div className="text-xs text-zinc-500 font-mono">Loading report sales…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <KpiTile icon={DollarSign} label="Reports sold" value={reportSales.reports_sold}
                         sub={`${reportSales.pending} started, unpaid`} tone="ok" testid="kpi-reports-sold" />
                <KpiTile icon={TrendingUp} label="Report revenue" value={usd(reportSales.revenue)}
                         sub="one-off certified reports" tone="ok" testid="kpi-report-revenue" />
                <KpiTile icon={Target} label="Checkout conversion"
                         value={reportSales.total_purchases ? pct((reportSales.reports_sold / reportSales.total_purchases) * 100) : "—"}
                         sub={`${reportSales.total_purchases} checkouts started`} testid="kpi-report-conv" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs" data-testid="report-sales-table">
                  <thead>
                    <tr className="text-zinc-500 font-mono uppercase text-[10px] tracking-wider border-b border-white/10">
                      <th className="py-2 pr-3">Buyer</th>
                      <th className="py-2 pr-3">VIN</th>
                      <th className="py-2 pr-3">Amount</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportSales.purchases.map((p) => (
                      <tr key={p.id} className="border-b border-white/5" data-testid={`report-sale-${p.id}`}>
                        <td className="py-2 pr-3 text-white">{p.email}</td>
                        <td className="py-2 pr-3 font-mono text-zinc-400">···{String(p.vin || "").slice(-6)}</td>
                        <td className="py-2 pr-3 font-mono">{p.amount != null ? usd(p.amount) : "—"}</td>
                        <td className="py-2 pr-3">
                          {p.status === "paid" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono"
                                  style={{ background: "rgba(57,255,20,0.1)", color: "#39FF14", border: "1px solid rgba(57,255,20,0.3)" }}>
                              PAID
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono"
                                  style={{ color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)" }}>
                              PENDING
                            </span>
                          )}
                        </td>
                        <td className="py-2 font-mono text-zinc-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                    {reportSales.purchases.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-4 text-zinc-500 font-mono">No report purchases yet — they'll appear the moment a shopper buys one.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SectionCard>

        {/* Charts row */}
        <div className="grid lg:grid-cols-3 gap-5">
          <SectionCard testid="chart-donut" title="Subscribers mix">
            <div className="h-64 relative">
              {planDonut.length ? (
                <>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={planDonut} dataKey="value" nameKey="name" innerRadius={62} outerRadius={90}
                           paddingAngle={3} stroke="rgba(0,0,0,0.6)" strokeWidth={2}>
                        {planDonut.map((s, i) => <Cell key={i} fill={s.color} fillOpacity={0.85} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: "#fff" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="font-mono text-3xl text-white font-medium">{totalSubs}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 mt-0.5">Accounts</div>
                  </div>
                </>
              ) : <EmptyChart /> }
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-mono">
              {planDonut.map((s) => (
                <span key={s.name} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/[0.03] border border-white/10">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-white uppercase tracking-wider">{s.name}</span>
                  <span className="text-zinc-500">{s.value}</span>
                  <span className="text-zinc-600">{totalSubs ? `${Math.round((s.value / totalSubs) * 100)}%` : ""}</span>
                </span>
              ))}
            </div>
          </SectionCard>

          <SectionCard testid="chart-signups"
            title="Signups"
            right={<RangeSelect value={rangeSignups} onChange={setRangeSignups} testid="range-signups" />}>
            <div className="h-64">
              {signupsFiltered.length ? (
                <ResponsiveContainer>
                  <AreaChart data={signupsFiltered}>
                    <defs>
                      <linearGradient id="signupsBasicFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#39FF14" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#39FF14" stopOpacity={0.03} />
                      </linearGradient>
                      <linearGradient id="signupsYearlyFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="signupsLifeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C084FC" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#C084FC" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="day" hide />
                    <YAxis stroke="#666" allowDecimals={false} tick={{ fontSize: 10, fontFamily: "monospace" }} width={26} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="basic" stackId="a" stroke="#39FF14" fill="url(#signupsBasicFill)" strokeWidth={2} name="Basic" />
                    <Area type="monotone" dataKey="yearly" stackId="a" stroke="#00E5FF" fill="url(#signupsYearlyFill)" strokeWidth={2} name="Yearly" />
                    <Area type="monotone" dataKey="lifetime" stackId="a" stroke="#C084FC" fill="url(#signupsLifeFill)" strokeWidth={2} name="Lifetime" />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyChart /> }
            </div>
          </SectionCard>

          <SectionCard testid="chart-revenue"
            title="Revenue · plan contribution"
            right={<RangeSelect value={rangeRevenue} onChange={setRangeRevenue} testid="range-revenue" />}>
            <div className="h-64">
              {revenueFiltered.length ? (
                <ResponsiveContainer>
                  <AreaChart data={revenueFiltered}>
                    <defs>
                      <linearGradient id="revBasicFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#39FF14" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#39FF14" stopOpacity={0.03} />
                      </linearGradient>
                      <linearGradient id="revYearlyFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="revLifeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C084FC" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#C084FC" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                    <YAxis stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={usdShort} width={40} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} formatter={(v) => usd(v)} />
                    <Area type="monotone" dataKey="basic_mrr" stackId="1" stroke="#39FF14" fill="url(#revBasicFill)" strokeWidth={2} name="Basic · $9.99/mo" />
                    <Area type="monotone" dataKey="yearly_mrr" stackId="1" stroke="#00E5FF" fill="url(#revYearlyFill)" strokeWidth={2} name="Yearly · $99.99/yr" />
                    <Area type="monotone" dataKey="lifetime_rev" stackId="1" stroke="#C084FC" fill="url(#revLifeFill)" strokeWidth={2} name="Lifetime · $199.99" />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyChart /> }
            </div>
          </SectionCard>
        </div>

        {/* Income statement + cash flow */}
        <div className="grid lg:grid-cols-2 gap-5">
          <SectionCard testid="income-statement"
            title="Income statement"
            right={<RangeSelect value={rangeIncome} onChange={setRangeIncome} testid="range-income" />}>
            <div className="grid grid-cols-2 gap-4">
              <IncomeCol label={`Last ${rangeIncome} days`} row={incomeThis} testid="income-period" />
              <IncomeCol label="YTD" row={incomeYtd} testid="income-ytd" />
            </div>
          </SectionCard>

          <SectionCard testid="chart-cashflow"
            title="Cash flow · cumulative"
            right={<RangeSelect value={rangeCashflow} onChange={setRangeCashflow} testid="range-cashflow" />}>
            <div className="h-64">
              {cashFiltered.length ? (
                <ResponsiveContainer>
                  <LineChart data={cashFiltered}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                    <YAxis stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={usdShort} width={50} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} formatter={(v) => usd(v)} />
                    <Line type="monotone" dataKey="cumulative" stroke="#39FF14" strokeWidth={2} dot={{ fill: "#39FF14", r: 3 }} name="Cumulative cash" />
                    <Line type="monotone" dataKey="net"        stroke="#00E5FF" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Net for month" />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyChart /> }
            </div>
          </SectionCard>
        </div>

        {/* Break-even */}
        <SectionCard testid="break-even" title="Break-even analysis">
          <div className="grid lg:grid-cols-3 gap-4 mb-4">
            <KpiTile icon={Target} label="Target users to break even" value={(be.target_users || 0).toLocaleString()}
                     sub={`@ ${usd(be.baseline_arpu)} ARPU / user`} tone="ok" testid="be-target" />
            <KpiTile icon={Users} label="Current paid users" value={(be.current_users || 0).toLocaleString()}
                     sub={`${be.users_to_break_even || 0} more needed`} testid="be-current" />
            <KpiTile icon={DollarSign} label={data?.expenses_source === "ledger" ? "Monthly costs (real, 3-mo avg)" : "Fixed monthly costs (est.)"} value={usd(be.fixed_monthly)}
                     sub={`+${usd(be.variable_per_user)}/user variable`} tone="warn" testid="be-fixed" />
          </div>
          <div className="h-72">
            {be.series?.length ? (
              <ResponsiveContainer>
                <LineChart data={be.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="users" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} label={{ value: "Paid users", position: "insideBottom", offset: -3, style: { fill: "#666", fontSize: 10 } }} />
                  <YAxis stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={usdShort} width={50} />
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} formatter={(v) => usd(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#39FF14" strokeWidth={2} dot={false} name="Revenue" />
                  <Line type="monotone" dataKey="cost"    stroke="#FFB020" strokeWidth={2} dot={false} name="Cost" />
                  {breakEvenPoint && (
                    <ReferenceDot x={breakEvenPoint.users} y={breakEvenPoint.revenue} r={5} fill="#00E5FF" stroke="#00E5FF" />
                  )}
                  <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyChart /> }
          </div>
        </SectionCard>

        {/* Projected sales */}
        <SectionCard
          testid="projections"
          title="Projected sales"
          right={
            <div className="relative shrink-0">
              <select
                value={projectionHorizon}
                onChange={(e) => setProjectionHorizon(Number(e.target.value))}
                data-testid="projection-horizon"
                aria-label="Projection horizon"
                className="appearance-none bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg pl-2.5 pr-6 py-1 text-[10px] font-mono uppercase tracking-wider text-white cursor-pointer"
              >
                <option value={3}  className="bg-black">Next 3 mo</option>
                <option value={6}  className="bg-black">Next 6 mo</option>
                <option value={12} className="bg-black">Next 12 mo · 1Y</option>
                <option value={24} className="bg-black">Next 24 mo · 2Y</option>
                <option value={36} className="bg-black">Next 36 mo · 3Y</option>
                <option value={48} className="bg-black">Next 48 mo · 4Y</option>
                <option value={60} className="bg-black">Next 60 mo · 5Y</option>
              </select>
              <ChevronDown className="w-3 h-3 text-zinc-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
            </div>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
            {[1, 2, 3, 4, 5].map((yr) => {
              const mile = proj.milestones?.[`${yr}yr`];
              if (!mile) return null;
              const tone = yr === 1 ? "ok" : "neutral";
              return (
                <div
                  key={yr}
                  data-testid={`projection-milestone-${yr}yr`}
                  className={`rounded-2xl border p-3 sm:p-4 ${tone === "ok" ? "border-[#39FF14]/25 bg-[#39FF14]/[0.04]" : "border-white/10 bg-white/[0.02]"}`}
                >
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                    Year {yr}
                  </div>
                  <div className="text-white font-semibold text-lg sm:text-xl leading-none">
                    {usdShort(mile.projected_mrr)} MRR
                  </div>
                  <div className="mt-1.5 text-[10px] sm:text-[11px] font-mono text-zinc-400 truncate">
                    {(mile.cum_users || 0).toLocaleString()} users · {usdShort(mile.projected_arr)} ARR
                  </div>
                </div>
              );
            })}
          </div>

          <div className="h-72">
            {proj.series?.length ? (
              <ResponsiveContainer>
                <AreaChart data={proj.series.slice(0, projectionHorizon)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={usdShort} width={50} />
                  <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} />
                  <Tooltip
                    contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(v, name) => {
                      if (name === "projected_mrr" || name === "projected_arr") return usd(v);
                      return v;
                    }}
                  />
                  <Area yAxisId="left"  type="monotone" dataKey="projected_arr" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.14} strokeWidth={2} name="Projected ARR" />
                  <Area yAxisId="left"  type="monotone" dataKey="projected_mrr" stroke="#39FF14" fill="#39FF14" fillOpacity={0.28} strokeWidth={2} name="Projected MRR" />
                  <Line yAxisId="right" type="monotone" dataKey="cum_users"     stroke="#FFB020" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Projected users" />
                  <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart /> }
          </div>

          <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed">
            Growth model: last-30-day signup rate compared to trailing 90-day average. Current monthly growth rate:{" "}
            <span className="font-mono text-white">
              {proj.monthly_growth_rate != null ? `${(proj.monthly_growth_rate * 100).toFixed(1)}%` : "—"}
            </span>{" "}
            · {proj.current_signup_rate_30d ?? 0} new signups in the last 30 days.
            Forecast assumes 60/40 monthly/yearly plan mix and current pricing. Real results will vary — treat as directional, not a guarantee.
          </div>
        </SectionCard>

        {/* Florida LLC Compliance Reports */}
        <SectionCard
          testid="compliance-section"
          title="Florida LLC compliance reports"
          right={
            <button
              onClick={() => downloadCompliance("all")}
              data-testid="compliance-download-all"
              className="brand-gradient text-black font-semibold px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> All (ZIP)
            </button>
          }
        >
          <div className="grid sm:grid-cols-2 gap-3">
            {manifest.map((r) => (
              <div
                key={r.key}
                data-testid={`compliance-report-${r.key}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-start justify-between gap-3 hover:border-white/25 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">{r.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{r.purpose}</div>
                </div>
                <button
                  onClick={() => downloadCompliance(r.key)}
                  data-testid={`compliance-download-${r.key}`}
                  className="shrink-0 p-2 rounded-lg border border-[#39FF14]/25 hover:bg-[#39FF14]/10 text-[#39FF14] transition-colors"
                  title={`Download ${r.title}`}
                  aria-label={`Download ${r.title}`}
                >
                  <Download className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>

          {/* LLC identity editor — collapsible */}
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02]">
            <button
              onClick={() => setLlcOpen(!llcOpen)}
              data-testid="llc-toggle"
              className="w-full flex items-center justify-between p-4 hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-zinc-500" strokeWidth={2} />
                <span className="text-sm text-white">LLC identity for report headers</span>
                {llc?.legal_name && (
                  <span className="hidden sm:inline text-[11px] font-mono text-zinc-500">
                    · {llc.legal_name} · EIN {llc.ein}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${llcOpen ? "rotate-180" : ""}`} />
            </button>
            {llcOpen && llc && (
              <div className="border-t border-white/5 p-4 space-y-3" data-testid="llc-editor">
                <div className="grid sm:grid-cols-2 gap-3">
                  <LlcField label="Legal LLC name" value={llc.legal_name}
                    onChange={(v) => { setLlc({ ...llc, legal_name: v }); setLlcDirty(true); }} testid="llc-legal-name" />
                  <LlcField label="DBA / trade name" value={llc.dba}
                    onChange={(v) => { setLlc({ ...llc, dba: v }); setLlcDirty(true); }} />
                  <LlcField label="Florida document number" value={llc.fl_doc_number}
                    onChange={(v) => { setLlc({ ...llc, fl_doc_number: v }); setLlcDirty(true); }} />
                  <LlcField label="EIN" value={llc.ein}
                    onChange={(v) => { setLlc({ ...llc, ein: v }); setLlcDirty(true); }} testid="llc-ein" />
                  <LlcField label="Formation date" value={llc.formation_date}
                    onChange={(v) => { setLlc({ ...llc, formation_date: v }); setLlcDirty(true); }} />
                  <LlcField label="Fiscal year end" value={llc.fiscal_year_end}
                    onChange={(v) => { setLlc({ ...llc, fiscal_year_end: v }); setLlcDirty(true); }} />
                </div>
                <LlcTextArea label="Principal place of business (multi-line)" value={llc.principal_address}
                  onChange={(v) => { setLlc({ ...llc, principal_address: v }); setLlcDirty(true); }} />
                <LlcTextArea label="Mailing address (leave blank to reuse principal)" value={llc.mailing_address}
                  onChange={(v) => { setLlc({ ...llc, mailing_address: v }); setLlcDirty(true); }} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <LlcField label="Registered agent name" value={llc.agent_name}
                    onChange={(v) => { setLlc({ ...llc, agent_name: v }); setLlcDirty(true); }} />
                  <LlcField label="Registered agent email" value={llc.agent_email}
                    onChange={(v) => { setLlc({ ...llc, agent_email: v }); setLlcDirty(true); }} />
                </div>
                <LlcTextArea label="Registered agent Florida address" value={llc.agent_address}
                  onChange={(v) => { setLlc({ ...llc, agent_address: v }); setLlcDirty(true); }} />

                <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-white/5">
                  <LlcField label="Accountant email (for monthly auto-send)" value={llc.accountant_email}
                    onChange={(v) => { setLlc({ ...llc, accountant_email: v }); setLlcDirty(true); }}
                    testid="llc-accountant-email" />
                  <label className="flex items-center gap-2 text-[11px] text-zinc-400 self-end pb-2">
                    <input
                      type="checkbox"
                      checked={!!llc.auto_email_enabled}
                      onChange={(e) => { setLlc({ ...llc, auto_email_enabled: e.target.checked }); setLlcDirty(true); }}
                      data-testid="llc-auto-email"
                      className="accent-[#39FF14]"
                    />
                    Auto-email compliance ZIP on the 1st of each month
                  </label>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2 mt-3">Members</div>
                  {(llc.members || []).map((m, i) => (
                    <div key={i} className="grid sm:grid-cols-4 gap-2 mb-2">
                      <input
                        value={m.name || ""}
                        onChange={(e) => {
                          const members = [...(llc.members || [])];
                          members[i] = { ...m, name: e.target.value };
                          setLlc({ ...llc, members });
                          setLlcDirty(true);
                        }}
                        placeholder="Name"
                        className="sm:col-span-2 bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-xs"
                      />
                      <input
                        value={m.role || ""}
                        onChange={(e) => {
                          const members = [...(llc.members || [])];
                          members[i] = { ...m, role: e.target.value };
                          setLlc({ ...llc, members });
                          setLlcDirty(true);
                        }}
                        placeholder="Role"
                        className="bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-xs"
                      />
                      <input
                        type="number"
                        value={m.ownership_pct || 0}
                        onChange={(e) => {
                          const members = [...(llc.members || [])];
                          members[i] = { ...m, ownership_pct: Number(e.target.value) };
                          setLlc({ ...llc, members });
                          setLlcDirty(true);
                        }}
                        placeholder="%"
                        className="bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-xs"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const members = [...(llc.members || []), { name: "", role: "Member", ownership_pct: 0 }];
                      setLlc({ ...llc, members });
                      setLlcDirty(true);
                    }}
                    className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
                  >
                    + Add member
                  </button>
                </div>
                <div className="pt-3 border-t border-white/5 flex justify-end">
                  <button
                    onClick={saveLlc}
                    disabled={!llcDirty || busy}
                    data-testid="llc-save"
                    className="text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-white"
                  >
                    {busy ? "Saving…" : (llcDirty ? "Save LLC identity" : "Saved")}
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
            Prepared for your Florida CPA or auditor. Not audited. Reports use ASC 606 revenue recognition
            for annual plans (recognized ratably over 12 months). Consult a licensed accountant before filing.
          </p>

          {/* Send now + 2FA management row */}
          <div className="mt-5 pt-5 border-t border-white/10 grid sm:grid-cols-2 gap-4">
            <SendToAccountant llc={llc} onError={setError} />
            <TwoFactorCard onLogout={onLogout} />
          </div>
        </SectionCard>

        {/* Expense vs Revenue — net position */}
        <ExpenseVsRevenueCard />

        {/* Operating expenses — Emergent payments → QuickBooks */}
        <OperatingExpensesCard />

        {/* Costs editor */}
        <SectionCard
          testid="costs-editor"
          title="Operating cost assumptions (fallback only — real logged expenses take priority in all charts)"
          right={
            <button
              onClick={saveCosts}
              disabled={!costsDirty || busy}
              data-testid="costs-save"
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-white"
            >
              {busy ? "Saving…" : (costsDirty ? "Save" : "Saved")}
            </button>
          }
        >
          {costs && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <CostInput label="Hosting / infra / SaaS ($/mo)" k="hosting_monthly" val={costs.hosting_monthly} onChange={(v) => { setCosts({ ...costs, hosting_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="Salaries ($/mo)"                k="salaries_monthly" val={costs.salaries_monthly} onChange={(v) => { setCosts({ ...costs, salaries_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="Marketing ($/mo)"               k="marketing_monthly" val={costs.marketing_monthly} onChange={(v) => { setCosts({ ...costs, marketing_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="Tools / software ($/mo)"        k="tools_monthly" val={costs.tools_monthly} onChange={(v) => { setCosts({ ...costs, tools_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="Other fixed ($/mo)"              k="other_fixed_monthly" val={costs.other_fixed_monthly} onChange={(v) => { setCosts({ ...costs, other_fixed_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="Variable / active user / mo ($)" k="variable_per_user_monthly" step={0.05} val={costs.variable_per_user_monthly} onChange={(v) => { setCosts({ ...costs, variable_per_user_monthly: v }); setCostsDirty(true); }} />
              <CostInput label="COGS · % of revenue"             k="cogs_pct_of_revenue" step={0.5} val={costs.cogs_pct_of_revenue} onChange={(v) => { setCosts({ ...costs, cogs_pct_of_revenue: v }); setCostsDirty(true); }} />
            </div>
          )}
        </SectionCard>

        {/* Continuity Flow — desktop ↔ mobile PWA handoff via QR */}
        <ContinuityCard />

        <div className="text-center text-[10px] font-mono text-zinc-600 pt-4 pb-8">
          © {new Date().getFullYear()} EVIQ LLC · Admin build · Confidential
        </div>
      </main>
    </div>
  );
};

const CostInput = ({ label, val, step = 1, onChange }) => (
  <label className="block">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
      {label}
    </div>
    <input
      type="number"
      step={step}
      value={val ?? 0}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-sm transition-colors"
    />
  </label>
);

const LlcField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
      {label}
    </div>
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-xs transition-colors"
    />
  </label>
);

const LlcTextArea = ({ label, value, onChange }) => (
  <label className="block">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
      {label}
    </div>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-3 py-2 text-white font-mono text-xs transition-colors resize-y"
    />
  </label>
);

const IncomeCol = ({ label, row = {}, testid }) => {
  const net = row.net_income || 0;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">{label}</div>
      <IncomeRow label="Revenue"        value={usd(row.revenue)}         />
      <IncomeRow label="− COGS"         value={usd(-Math.abs(row.cogs || 0))} />
      <IncomeRow label="− Operating"    value={usd(-Math.abs(row.opex || 0))} />
      <div className="border-t border-white/10 my-2" />
      <IncomeRow label="Net income"     value={usd(net)} bold color={net >= 0 ? "#39FF14" : "#FF6B57"} />
    </div>
  );
};

const IncomeRow = ({ label, value, bold, color }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs text-zinc-400">{label}</span>
    <span className={`font-mono text-sm ${bold ? "font-semibold" : ""}`} style={{ color: color || "#fff" }}>
      {value}
    </span>
  </div>
);

const EmptyChart = () => (
  <div className="h-full flex items-center justify-center text-xs text-zinc-500 font-mono">
    No data yet — seed demo subscribers below to populate charts.
  </div>
);

// ============================================================================
// Compliance sub-components
// ============================================================================
const SendToAccountant = ({ llc, onError }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async () => {
    if (!llc?.accountant_email) {
      setMsg("Set accountant email above first");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const r = await fetch(`${API}/api/admin/compliance/send-monthly-now`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
      });
      if (r.ok) {
        setMsg(`Sent to ${llc.accountant_email}`);
      } else {
        const d = await r.json().catch(() => ({}));
        onError(d.detail || "Send failed");
      }
    } catch {
      onError("Network error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="send-accountant-card">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2} />
        <span className="text-sm text-white font-medium">Send compliance package now</span>
      </div>
      <div className="text-[11px] text-zinc-500 leading-relaxed mb-3">
        Emails the full 7-report ZIP to <span className="font-mono text-zinc-300">{llc?.accountant_email || "—"}</span> via Resend.
      </div>
      <button
        onClick={send}
        disabled={busy || !llc?.accountant_email}
        data-testid="send-accountant-btn"
        className="w-full brand-gradient text-black font-semibold rounded-lg px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={2.5} />}
        {busy ? "Sending…" : "Email now"}
      </button>
      {msg && (
        <div className="mt-2 text-[11px] text-[#39FF14] font-mono" data-testid="send-accountant-msg">{msg}</div>
      )}
    </div>
  );
};

const TwoFactorCard = ({ onLogout }) => {
  const [status, setStatus] = useState(null);
  const [enrollData, setEnrollData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newCodes, setNewCodes] = useState(null);

  const regenCodes = async () => {
    setErr("");
    setBusy(true);
    const r = await fetch(`${API}/api/admin/totp/recovery/regenerate`, {
      method: "POST", credentials: "include", headers: authHeaders(),
    });
    setBusy(false);
    if (!r.ok) { setErr("Regeneration failed"); return; }
    setNewCodes((await r.json()).recovery_codes);
    await loadStatus();
  };

  const loadStatus = async () => {
    const r = await fetch(`${API}/api/admin/totp/status`, { credentials: "include", headers: authHeaders() });
    if (r.status === 401) { onLogout(); return; }
    if (r.ok) setStatus(await r.json());
  };
  useEffect(() => { loadStatus(); }, []);

  const startEnroll = async () => {
    setErr("");
    setBusy(true);
    const r = await fetch(`${API}/api/admin/totp/enroll`, { method: "POST", credentials: "include", headers: authHeaders() });
    setBusy(false);
    if (!r.ok) { setErr("Enroll failed"); return; }
    setEnrollData(await r.json());
  };

  const verifyEnroll = async () => {
    setErr("");
    setBusy(true);
    const r = await fetch(`${API}/api/admin/totp/verify-enroll`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ code: code.trim() }),
    });
    setBusy(false);
    if (!r.ok) { setErr("Invalid code — try again"); return; }
    setEnrollData(null);
    setCode("");
    await loadStatus();
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="totp-card">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2} />
        <span className="text-sm text-white font-medium">Two-factor authentication</span>
        {status?.enabled && (
          <span className="ml-auto text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14]" data-testid="totp-status">
            Enabled
          </span>
        )}
      </div>

      {status?.enabled && !enrollData && (
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Login now requires a 6-digit code from your authenticator app.<br />
          Recovery codes remaining: <span className="text-white font-mono">{status.recovery_codes_remaining}</span>
          <button onClick={regenCodes} disabled={busy}
            data-testid="totp-regen-codes-btn"
            className="mt-2 w-full px-3 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-zinc-300 text-[10px] font-mono uppercase tracking-wider disabled:opacity-40 transition-colors">
            {busy ? "Generating…" : "Regenerate recovery codes"}
          </button>
          {newCodes && (
            <div className="mt-2" data-testid="totp-new-codes">
              <div className="text-[10px] text-[#FFB020] leading-relaxed mb-1">
                ⚠️ NEW codes — old ones no longer work. SAVE these now (shown once, each works once):
              </div>
              <div className="grid grid-cols-2 gap-1 text-[11px] font-mono text-white bg-black/60 border border-white/10 rounded-lg p-2">
                {newCodes.map((c, i) => <div key={i}>{c}</div>)}
              </div>
            </div>
          )}
          {err && <div className="mt-1 text-[11px] text-[#FF6B57]">{err}</div>}
        </div>
      )}

      {!status?.enabled && !enrollData && (
        <>
          <div className="text-[11px] text-zinc-500 leading-relaxed mb-3">
            Protect your admin account with Google Authenticator, Authy, or 1Password.
          </div>
          <button onClick={startEnroll} disabled={busy}
            data-testid="totp-enable-btn"
            className="w-full px-3 py-2 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-white text-xs font-mono uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />}
            Enable 2FA
          </button>
        </>
      )}

      {enrollData && (
        <div className="space-y-3" data-testid="totp-enroll">
          <div className="text-[11px] text-zinc-400 leading-relaxed">
            1. Scan this QR code with your authenticator app.
          </div>
          <div className="rounded-lg bg-white p-2 flex items-center justify-center">
            <img src={enrollData.qr_data_url} alt="TOTP QR" className="w-40 h-40" />
          </div>
          <div className="text-[10px] font-mono text-zinc-500 break-all">
            Secret: <span className="text-zinc-300">{enrollData.secret}</span>
          </div>
          <div className="text-[10px] text-[#FFB020] leading-relaxed">
            ⚠️ SAVE these 8 recovery codes (each works ONCE if you lose your phone):
          </div>
          <div className="grid grid-cols-2 gap-1 text-[11px] font-mono text-white bg-black/60 border border-white/10 rounded-lg p-2">
            {enrollData.recovery_codes.map((c, i) => (
              <div key={i}>{c}</div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-400">2. Enter the 6-digit code from your app:</div>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="000000"
            data-testid="totp-enroll-code"
            className="w-full bg-black border border-white/10 hover:border-white/25 focus:border-[#39FF14]/50 rounded-lg px-3 py-2 text-white font-mono text-center tracking-widest" />
          {err && <div className="text-[11px] text-[#FF6B57]">{err}</div>}
          <button onClick={verifyEnroll} disabled={busy || code.length !== 6}
            data-testid="totp-verify-btn"
            className="w-full brand-gradient text-black font-semibold rounded-lg px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? "Verifying…" : "Confirm & enable"}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ROOT
// ============================================================================
export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  useEffect(() => {
    api.me().then((r) => setAuthed(r.ok)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return authed
    ? <Dashboard onLogout={() => setAuthed(false)} />
    : <LoginCard onSuccess={() => setAuthed(true)} />;
}
